import React, { useState, useEffect } from 'react';
import { supportAPI } from '../services/api';
import { format } from 'date-fns';

const SupportMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [showReplyBox, setShowReplyBox] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [filters.status]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const response = await supportAPI.list(params);
      setMessages(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch support messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await supportAPI.stats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMessages();
  };

  const handleMarkRead = async (id) => {
    try {
      await supportAPI.markRead(id);
      fetchMessages();
      fetchStats();
      if (selectedMessage?.id === id) {
        setSelectedMessage({ ...selectedMessage, status: 'read' });
      }
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkReplied = async (id) => {
    try {
      await supportAPI.markReplied(id);
      fetchMessages();
      fetchStats();
      if (selectedMessage?.id === id) {
        setSelectedMessage({ ...selectedMessage, status: 'replied' });
      }
    } catch (err) {
      console.error('Failed to mark as replied:', err);
    }
  };

  const handleClose = async (id) => {
    try {
      await supportAPI.close(id);
      fetchMessages();
      fetchStats();
      if (selectedMessage?.id === id) {
        setSelectedMessage({ ...selectedMessage, status: 'closed' });
      }
    } catch (err) {
      console.error('Failed to close message:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await supportAPI.delete(id);
      fetchMessages();
      fetchStats();
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;

    setSending(true);
    try {
      await supportAPI.reply(selectedMessage.id, { reply: replyText });
      setReplyText('');
      setShowReplyBox(false);
      setSelectedMessage({ ...selectedMessage, status: 'replied' });
      fetchMessages();
      fetchStats();
      alert('Reply sent successfully!');
    } catch (err) {
      console.error('Failed to send reply:', err);
      alert('Failed to send reply: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: 'danger',
      read: 'warning',
      replied: 'success',
      closed: 'gray'
    };
    return <span className={`badge badge-${colors[status] || 'gray'}`}>{status}</span>;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Support Messages</h1>
          <p className="page-description">View and manage customer inquiries</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--danger-600)' }}>{stats.new || 0}</div>
            <div className="stat-label">New</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--warning-600)' }}>{stats.read || 0}</div>
            <div className="stat-label">Read</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--success-600)' }}>{stats.replied || 0}</div>
            <div className="stat-label">Replied</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--gray-600)' }}>{stats.closed || 0}</div>
            <div className="stat-label">Closed</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        {/* Messages List */}
        <div className="card" style={{ flex: selectedMessage ? '0 0 50%' : '1' }}>
          {/* Filters */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <select
              className="form-select"
              style={{ width: 'auto' }}
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
              <option value="closed">Closed</option>
            </select>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search by email or name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn btn-secondary">Search</button>
            </form>
          </div>

          {loading ? (
            <div className="loading-container"><div className="spinner"></div></div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-500)' }}>
              No support messages found
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Sender</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => (
                    <tr
                      key={msg.id}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selectedMessage?.id === msg.id ? 'var(--primary-50)' :
                                        msg.status === 'new' ? 'var(--danger-50)' : 'inherit'
                      }}
                      onClick={() => {
                        setSelectedMessage(msg);
                        if (msg.status === 'new') {
                          handleMarkRead(msg.id);
                        }
                      }}
                    >
                      <td>
                        <div>
                          <span className="font-medium">{msg.name || 'Anonymous'}</span>
                          <br />
                          <small style={{ color: 'var(--gray-500)' }}>{msg.email}</small>
                        </div>
                      </td>
                      <td style={{ maxWidth: '200px' }}>
                        <span style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {msg.message}
                        </span>
                        {msg.attachments?.length > 0 && (
                          <div style={{ marginTop: '0.25rem' }}>
                            <small style={{ color: 'var(--gray-500)' }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                              </svg>
                              {msg.attachments.length} file{msg.attachments.length > 1 ? 's' : ''}
                            </small>
                          </div>
                        )}
                      </td>
                      <td>{getStatusBadge(msg.status)}</td>
                      <td>
                        <small>{format(new Date(msg.created_at), 'MMM d, yyyy')}</small>
                        <br />
                        <small style={{ color: 'var(--gray-500)' }}>{format(new Date(msg.created_at), 'HH:mm')}</small>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          {msg.status === 'new' && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleMarkRead(msg.id)}
                              title="Mark as Read"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                          )}
                          {(msg.status === 'new' || msg.status === 'read') && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleMarkReplied(msg.id)}
                              title="Mark as Replied"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </button>
                          )}
                          {msg.status !== 'closed' && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleClose(msg.id)}
                              title="Close"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                              </svg>
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(msg.id)}
                            title="Delete"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Message Detail Panel */}
        {selectedMessage && (
          <div className="card" style={{ flex: '0 0 45%', position: 'sticky', top: '1rem', height: 'fit-content', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>{selectedMessage.name || 'Anonymous'}</h3>
                <a href={`mailto:${selectedMessage.email}`} style={{ color: 'var(--primary-600)' }}>
                  {selectedMessage.email}
                </a>
              </div>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedMessage(null)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              {getStatusBadge(selectedMessage.status)}
              <span className="badge badge-gray">
                {format(new Date(selectedMessage.created_at), 'MMM d, yyyy HH:mm')}
              </span>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--gray-600)', fontSize: '0.875rem', textTransform: 'uppercase' }}>Message</h4>
              <div style={{
                padding: '1rem',
                background: 'var(--gray-50)',
                borderRadius: '8px',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6'
              }}>
                {selectedMessage.message}
              </div>
            </div>

            {/* Attachments */}
            {selectedMessage.attachments?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--gray-600)', fontSize: '0.875rem', textTransform: 'uppercase' }}>
                  Attachments ({selectedMessage.attachments.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedMessage.attachments.map((att) => (
                    <a
                      key={att.id}
                      href={att.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        background: 'var(--gray-50)',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        color: 'inherit',
                        border: '1px solid var(--gray-200)',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-300)'}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--gray-200)'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary-600)" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {att.original_filename}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                          {formatFileSize(att.file_size)}
                        </div>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Reply Section */}
            {showReplyBox ? (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--gray-600)', fontSize: '0.875rem', textTransform: 'uppercase' }}>
                  Reply to {selectedMessage.name || selectedMessage.email}
                </h4>
                <textarea
                  className="form-input"
                  rows="5"
                  placeholder="Type your reply here..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  style={{ width: '100%', marginBottom: '0.75rem', resize: 'vertical' }}
                  disabled={sending}
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleSendReply}
                    disabled={sending || !replyText.trim()}
                    style={{ flex: 1 }}
                  >
                    {sending ? (
                      <>Sending...</>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.5rem' }}>
                          <line x1="22" y1="2" x2="11" y2="13" />
                          <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                        Send Reply
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setShowReplyBox(false); setReplyText(''); }}
                    disabled={sending}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowReplyBox(true)}
                  style={{ width: '100%' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.5rem' }}>
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                  </svg>
                  Write Reply
                </button>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <a
                href={`mailto:${selectedMessage.email}?subject=Re: Your inquiry to KrossIQ&body=%0A%0A---%0AOriginal message:%0A${encodeURIComponent(selectedMessage.message)}`}
                className="btn btn-secondary"
                style={{ flex: 1 }}
                title="Open in email client"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.5rem' }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Open Email Client
              </a>
              {selectedMessage.status !== 'closed' && (
                <button
                  className="btn btn-warning"
                  onClick={() => handleClose(selectedMessage.id)}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportMessages;
